import React, { useEffect, useState } from "react";
import { BadgeCheck, Camera, Loader2, Save, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const VERTICAL_AR = { UNDEFINED: "غير محدد", OTHER: "أخرى", AUTO: "سيارات", BEAUTY: "تجميل", APPAREL: "أزياء", EDU: "تعليم", ENTERTAIN: "ترفيه", EVENT_PLAN: "تنظيم فعاليات", FINANCE: "مالية", GROCERY: "بقالة", GOVT: "حكومي", HOTEL: "فنادق", HEALTH: "صحة", NONPROFIT: "غير ربحي", PROF_SERVICES: "خدمات مهنية", RETAIL: "تجزئة", TRAVEL: "سفر", RESTAURANT: "مطاعم", NOT_A_BIZ: "ليس نشاطًا" };

const Field = ({ label, hint, children }) => (
  <label className="block text-xs space-y-1"><span className="font-bold">{label}</span>{hint && <span className="text-muted-foreground"> — {hint}</span>}{children}</label>
);

export const WhatsAppProfile = () => {
  const [numbers, setNumbers] = useState([]);
  const [pid, setPid] = useState("");
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/wa/numbers").then((r) => {
      const ready = (r.data.numbers || []).filter((n) => n.ready);
      setNumbers(ready);
      setPid(r.data.active?.phone_id || ready[0]?.id || "");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!pid) return;
    setData(null);
    api.get("/wa/profile", { params: { phone_id: pid } }).then((r) => {
      setData(r.data);
      const p = r.data.profile || {};
      setForm({ about: p.about || "", description: p.description || "", address: p.address || "", email: p.email || "", vertical: p.vertical || "UNDEFINED", web1: p.websites?.[0] || "", web2: p.websites?.[1] || "" });
    }).catch((e) => toast.error(formatError(e)));
  }, [pid]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/wa/profile", { phone_id: pid, about: form.about, description: form.description, address: form.address, email: form.email, vertical: form.vertical, websites: [form.web1, form.web2] });
      setData(r.data); toast.success("تم تحديث ملف واتساب");
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const upload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try { const r = await api.post("/wa/profile/picture", { phone_id: pid, image: reader.result }); setData(r.data); toast.success("تم تحديث صورة الحساب"); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ph = data?.phone || {};

  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="wa-profile">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-emerald-500" /><div><div className="font-bold text-sm">ملف حساب واتساب للأعمال</div><div className="text-[11px] text-muted-foreground">الصورة والوصف والعنوان والموقع كما يراها العملاء في واتساب</div></div></div>
        {numbers.length > 1 && (
          <select value={pid} onChange={(e) => setPid(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" dir="ltr" data-testid="wa-profile-number-select">
            {numbers.map((n) => <option key={n.id} value={n.id}>{n.display_phone_number} · {n.verified_name}</option>)}
          </select>
        )}
      </div>
      {!data && <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> جارٍ التحميل من Meta…</div>}
      {data && (
        <div className="grid md:grid-cols-[200px_1fr] gap-5">
          <div className="space-y-3 text-center">
            <div className="relative mx-auto w-36 h-36 rounded-full overflow-hidden border border-border bg-secondary">
              {data.profile?.profile_picture_url ? <img src={data.profile.profile_picture_url} alt="" className="w-full h-full object-cover" data-testid="wa-profile-picture" /> : <Phone className="w-10 h-10 m-auto mt-12 text-muted-foreground" />}
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer rounded-full border border-border px-3 py-1.5 hover:bg-secondary" data-testid="wa-profile-picture-upload">
              <Camera className="w-3.5 h-3.5" /> تغيير الصورة
              <input type="file" accept="image/png,image/jpeg" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
            </label>
            <div className="text-[11px] text-muted-foreground">مربعة، يُفضّل 640×640 — تُقصّ وتُصغَّر تلقائيًا</div>
            <div className="rounded-lg border border-border p-2 text-[11px] text-start space-y-0.5">
              <div dir="ltr" className="font-bold">{ph.display_phone_number}</div>
              <div>الاسم المعروض: <b>{ph.verified_name}</b></div>
              <div className="text-muted-foreground">تغيير الاسم المعروض يتم من WhatsApp Manager (يخضع لمراجعة Meta).</div>
            </div>
          </div>
          <div className="space-y-3">
            <Field label="نبذة (About)" hint="حتى 139 حرفًا، تظهر تحت الاسم"><Input value={form.about} onChange={set("about")} maxLength={139} className="text-sm" dir="auto" data-testid="wa-profile-about" /></Field>
            <Field label="الوصف" hint="حتى 512 حرفًا"><Textarea value={form.description} onChange={set("description")} maxLength={512} rows={3} className="text-sm" dir="auto" data-testid="wa-profile-description" /></Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="البريد"><Input value={form.email} onChange={set("email")} className="text-sm" dir="ltr" data-testid="wa-profile-email" /></Field>
              <Field label="التصنيف">
                <select value={form.vertical} onChange={set("vertical")} className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" data-testid="wa-profile-vertical">
                  {(data.verticals || []).map((v) => <option key={v} value={v}>{VERTICAL_AR[v] || v}</option>)}
                </select>
              </Field>
              <Field label="الموقع 1"><Input value={form.web1} onChange={set("web1")} className="text-sm" dir="ltr" data-testid="wa-profile-web1" /></Field>
              <Field label="الموقع 2"><Input value={form.web2} onChange={set("web2")} className="text-sm" dir="ltr" data-testid="wa-profile-web2" /></Field>
            </div>
            <Field label="العنوان" hint="حتى 256 حرفًا"><Input value={form.address} onChange={set("address")} maxLength={256} className="text-sm" dir="auto" data-testid="wa-profile-address" /></Field>
            <Button onClick={save} disabled={busy} className="gap-1" data-testid="wa-profile-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} حفظ في واتساب</Button>
          </div>
        </div>
      )}
    </div>
  );
};
